<template>
 <ModalConsulte ref="modalRef" :hide-footer="false" modalWidth="700px !important">
  <template #header>
   <h4 class="modal-title">{{ tituloModal }}</h4>
  </template>
  <template #content>
   <div class="row g-3">
    <div class="col-md-6">
     <label class="form-label" for="campo-exemplar-45e2">Exemplar</label>
     <!--
       Busca, e não campo livre: o tombamento era digitado de cabeça, e errar um dígito só
       aparecia como "Exemplar não encontrado", sem meio de descobrir o número certo sem sair da
       tela. O leitor de código de barras continua funcionando — digitar o tombamento inteiro
       filtra para uma opção só.
     -->
     <MultiSelect
      id="campo-exemplar-45e2"
      v-model="bibExemplarSelecionado"
      mode="single"
      :options="pesquisarExemplares"
      :filter-results="false"
      :min-chars="0"
      :resolve-on-load="true"
      :delay="300"
      :searchable="true"
      :can-clear="true"
      value-prop="bibExemplarId"
      label="descricao"
      track-by="descricao"
      no-options-text="Digite o tombamento, o código de barras ou o título"
      no-results-text="Nenhum exemplar encontrado nesta biblioteca"
      placeholder="Pesquise pelo tombamento, código de barras ou título"
      aria-label="Pesquisar exemplar por tombamento, código de barras ou título"
      @change="selecionarExemplar"
     />
     <small v-if="exemplarDescricao" class="text-muted d-block">{{ exemplarDescricao }}</small>
     <small v-if="avisoExemplar" class="text-danger d-block">{{ avisoExemplar }}</small>
    </div>
    <div class="col-md-3">
     <label class="form-label" for="campo-dataEmprestimo-45e2">Data de Empréstimo</label>
     <input v-model="form.dataEmprestimo" type="date" class="form-control" @change="recalcularVencimento" id="campo-dataEmprestimo-45e2" />
    </div>
    <div class="col-md-3">
     <label class="form-label" for="campo-dataVencimento-45e2">Data de Vencimento</label>
     <input v-model="form.dataVencimento" type="date" class="form-control" readonly id="campo-dataVencimento-45e2" />
    </div>
    <div class="col-md-12">
     <div class="form-check mt-2">
      <input id="especial" v-model="especialMarcado" class="form-check-input" type="checkbox" />
      <label class="form-check-label" for="especial">Especial</label>
     </div>
    </div>
    <div class="col-md-12">
     <label class="form-label" for="campo-observacao-45e2">Observação</label>
     <textarea v-model="form.observacao" rows="4" class="form-control" id="campo-observacao-45e2"></textarea>
    </div>
   </div>
   <div v-if="mensagemErro" class="alert alert-danger mt-3 mb-0">
    {{ mensagemErro }}
   </div>
  </template>
  <template #actions>
   <button
    v-if="isEdicao() && permissaoTela.excluir"
    type="button"
    class="btn btn-outline-danger"
    :disabled="salvando || excluindo"
    @click="excluir"
   >
    {{ excluindo ? "Excluindo..." : "Excluir" }}
   </button>
   <button type="button" class="btn btn-light" :disabled="salvando || excluindo" @click="close">Cancelar</button>
   <button
    v-if="isEdicao() ? permissaoTela.alterar : permissaoTela.incluir"
    type="button"
    class="btn btn-primary"
    :disabled="salvando || excluindo"
    @click="salvar"
   >
    {{ salvando ? "Salvando..." : "OK" }}
   </button>
  </template>
 </ModalConsulte>
</template>

<script lang="ts">
import dialogoService from '@/services/globals/dialogo.service';
import { defineComponent } from "vue";
import ModalConsulte from "@/components/modal/ModalConsulte.vue";
import { BibEmprestimoModel } from "@/models/biblioteca/bibemprestimo/bibemprestimo.model";
import { BibExemplarModel } from "@/models/biblioteca/bibexemplar/bibexemplar.model";
import BibEmprestimoService from "@/services/biblioteca/bibemprestimo/bibemprestimo.service";
import { BibExemplarPesquisaModel } from "@/models/biblioteca/bibexemplar/bibexemplar-pesquisa.model";
import BibExemplarService from "@/services/biblioteca/bibexemplar/bibexemplar.service";
import BibTipoUsuarioService from "@/services/biblioteca/bibtipousuario/bibtipousuario.service";
import { useStore } from "@/store";
import { addBusinessDaysFromLoan, formatDateInput } from "./utils";
import {
 PERMISSAO_TELA_INJECT_KEY,
 permissaoTelaLiberada,
} from "@/composables/usePermissaoTela/usePermissaoTela";

type ModalContext = {
 bibTipoUsuarioId: number;
 segUsuarioId: number;
};

/** Uma opção do combo de busca: o exemplar mais o rótulo que o bibliotecário lê. */
type ExemplarOpcao = BibExemplarPesquisaModel & { descricao: string };

export default defineComponent({
 name: "ModalCadastroEdicaoEmprestimo",
 components: { ModalConsulte },
 emits: ["saved"],
 inject: {
  // Permissão da tela de empréstimo que abriu o modal.
  permissaoTela: {
   from: PERMISSAO_TELA_INJECT_KEY,
   default: () => permissaoTelaLiberada(),
  },
 },
 data() {
  return {
   tituloModal: "Cadastrar Empréstimo",
   tombamento: "",
   /** Id do exemplar escolhido na busca — é o v-model do combo. */
   bibExemplarSelecionado: null as number | null,
   /** Opções já vistas, para o combo saber redesenhar a escolhida sem refazer a busca. */
   opcoesExemplar: [] as ExemplarOpcao[],
   exemplarDescricao: "",
   avisoExemplar: "",
   tombamentoPesquisado: "",
   mensagemErro: "",
   salvando: false,
   excluindo: false,
   especialMarcado: false,
   contexto: {
    bibTipoUsuarioId: 0,
    segUsuarioId: 0,
   } as ModalContext,
   model: new BibEmprestimoModel() as BibEmprestimoModel,
   exemplar: null as BibExemplarModel | null,
   form: {
    dataEmprestimo: "",
    dataVencimento: "",
    observacao: "",
   },
  };
 },
 methods: {
  async open(contexto: ModalContext, row?: BibEmprestimoModel) {
   this.contexto = contexto;
   this.mensagemErro = "";
   this.avisoExemplar = "";
   this.excluindo = false;
   this.exemplar = null;
   this.exemplarDescricao = "";
   this.tombamentoPesquisado = "";
   this.bibExemplarSelecionado = null;
   this.opcoesExemplar = [];

   const currentRow = row ?? new BibEmprestimoModel();
   const bibEmprestimoId = Number((row as any)?.bibEmprestimoId ?? (row as any)?.bibEmprestimoid ?? 0);
   const bibExemplarId = Number((row as any)?.bibExemplarId ?? (row as any)?.bibExemplarid ?? 0);

   if (bibEmprestimoId) {
    this.tituloModal = "Editar Empréstimo";
    this.model = { ...new BibEmprestimoModel(), ...currentRow, bibEmprestimoId };
    this.model.bibTipoUsuarioId = Number(this.model.bibTipoUsuarioId || this.contexto.bibTipoUsuarioId || 0);
    await this.carregarParametrosTipoUsuario(this.model.bibTipoUsuarioId);
    this.model.titulo = this.normalizeTitulo(currentRow.titulo);
    this.form.dataEmprestimo = formatDateInput(currentRow.dataEmprestimo);
    this.form.dataVencimento = formatDateInput(currentRow.dataVencimento);
    this.form.observacao = currentRow.observacao ?? "";
    this.especialMarcado = Number(currentRow.especial) === 1;

    if (bibExemplarId) {
     const exemplar = await BibExemplarService.get(bibExemplarId);
     this.exemplar = exemplar;
     this.tombamento = exemplar.identificacao ?? "";
     this.exemplarDescricao = this.formatExemplarDescricao(this.tombamento, this.model.titulo);
     this.tombamentoPesquisado = this.normalizeTombamento(this.tombamento);

     // O combo é assíncrono: sem a opção correspondente na lista ele não teria o que mostrar
     // para o exemplar já vinculado, e a edição abriria com o campo aparentemente vazio.
     this.opcoesExemplar = [
      this.montarOpcao({
       bibExemplarId,
       gesEscolaId: (exemplar as any)?.gesEscolaId ?? null,
       identificacao: exemplar.identificacao ?? "",
       codigoBarra: (exemplar as any)?.codigoBarra ?? "",
       titulo: this.model.titulo,
       autoria: currentRow.autoria ?? "",
       emprestado: false,
       baixado: Number((exemplar as any)?.baixado) === 1,
       podeEmprestar: Number((exemplar as any)?.podeEmprestar) === 1,
      }),
     ];
     this.bibExemplarSelecionado = bibExemplarId;
    } else {
     this.tombamento = "";
    }
   } else {
    this.tituloModal = "Cadastrar Empréstimo";
    this.model = new BibEmprestimoModel();
    this.model.bibTipoUsuarioId = Number(this.contexto.bibTipoUsuarioId || 0);
    await this.carregarParametrosTipoUsuario(this.model.bibTipoUsuarioId);
    this.tombamento = "";
    this.tombamentoPesquisado = "";
    this.especialMarcado = false;
    this.form.dataEmprestimo = formatDateInput(new Date());
    this.form.dataVencimento = addBusinessDaysFromLoan(this.form.dataEmprestimo, this.getPrazoEmprestimo());
    this.form.observacao = "";
   }

   (this.$refs.modalRef as any)?.open();
  },
  close() {
   (this.$refs.modalRef as any)?.close();
  },
  recalcularVencimento() {
   this.form.dataVencimento = addBusinessDaysFromLoan(this.form.dataEmprestimo, this.getPrazoEmprestimo());
  },
  normalizeTombamento(valor: string) {
   return (valor ?? "").trim();
  },
  normalizeTitulo(valor: string | null | undefined) {
   return (valor ?? "").toString().trim();
  },
  formatExemplarDescricao(identificacao: string | null | undefined, titulo: string | null | undefined) {
   const identificacaoNormalizada = this.normalizeTombamento((identificacao ?? "").toString());
   const tituloNormalizado = this.normalizeTitulo(titulo);

   if (identificacaoNormalizada && tituloNormalizado) {
    return `${identificacaoNormalizada} - ${tituloNormalizado}`;
   }

   return identificacaoNormalizada || tituloNormalizado;
  },
  getPrazoEmprestimo() {
   const prazo = Number(this.model.bibTipoUsuario?.prazoEmprestimo ?? this.model.prazoEmprestimo ?? 0);
   return Number.isFinite(prazo) && prazo > 0 ? prazo : 15;
  },
  async carregarParametrosTipoUsuario(bibTipoUsuarioIdRaw?: number | string | null) {
   const bibTipoUsuarioId = Number(bibTipoUsuarioIdRaw ?? this.model.bibTipoUsuarioId ?? 0);
   if (!bibTipoUsuarioId) {
    this.model.bibTipoUsuario = null;
    this.model.prazoEmprestimo = 0;
    this.model.moraAtraso = 0;
    return;
   }

   if (Number(this.model.bibTipoUsuario?.bibTipoUsuarioId || 0) === bibTipoUsuarioId) {
    return;
   }

   try {
    const tipoUsuario = await BibTipoUsuarioService.get(bibTipoUsuarioId);
    this.model.bibTipoUsuario = tipoUsuario;
    this.model.prazoEmprestimo = Number(tipoUsuario?.prazoEmprestimo ?? 0);
    this.model.moraAtraso = Number(tipoUsuario?.moraAtraso ?? 0);
   } catch (error) {
    this.model.bibTipoUsuario = null;
    this.model.prazoEmprestimo = 0;
    this.model.moraAtraso = 0;
    console.error("Erro ao carregar parâmetros do tipo de usuário:", error);
   }
  },
  /** "TOMB-123 - Dom Casmurro (Assis, Machado de)" — o que o bibliotecário lê na lista. */
  montarOpcao(exemplar: BibExemplarPesquisaModel): ExemplarOpcao {
   const tombamento = this.normalizeTombamento((exemplar.identificacao ?? "").toString());
   const titulo = this.normalizeTitulo(exemplar.titulo);
   const autoria = this.normalizeTitulo(exemplar.autoria);

   const partes = [tombamento, titulo].filter(Boolean);
   let descricao = partes.join(" - ") || String(exemplar.bibExemplarId);

   if (autoria) {
    descricao = `${descricao} (${autoria})`;
   }

   if (exemplar.emprestado) {
    descricao = `${descricao} — emprestado`;
   }

   return { ...exemplar, descricao };
  },

  /**
   * Alimenta o combo de exemplares. O `@vueform/multiselect` chama esta função a cada busca
   * (`filter-results: false`), então quem filtra é o servidor — e é ele quem limita à biblioteca
   * da tela, que é a única de onde se pode emprestar.
   */
  async pesquisarExemplares(termo: string): Promise<ExemplarOpcao[]> {
   try {
    const encontrados = await BibExemplarService.pesquisar(
     (termo ?? "").toString().trim(),
     this.getGesEscolaTela()
    );

    this.opcoesExemplar = encontrados.map((item) => this.montarOpcao(item));
    return this.opcoesExemplar;
   } catch (error) {
    console.error("Erro ao pesquisar exemplares:", error);
    return [];
   }
  },

  /** O bibliotecário escolheu (ou limpou) um exemplar na busca. */
  selecionarExemplar(valor: number | null) {
   this.mensagemErro = "";
   this.avisoExemplar = "";

   const escolhido = this.opcoesExemplar.find(
    (opcao) => Number(opcao.bibExemplarId) === Number(valor)
   );

   if (!escolhido) {
    this.exemplar = null;
    this.exemplarDescricao = "";
    this.tombamento = "";
    this.tombamentoPesquisado = "";
    this.model.titulo = "";
    return;
   }

   this.exemplar = {
    bibExemplarId: escolhido.bibExemplarId,
    gesEscolaId: escolhido.gesEscolaId ?? 0,
    identificacao: escolhido.identificacao ?? "",
    codigoBarra: escolhido.codigoBarra ?? "",
   } as unknown as BibExemplarModel;

   (this.exemplar as any).emprestado = escolhido.emprestado;

   this.tombamento = this.normalizeTombamento((escolhido.identificacao ?? "").toString());
   this.tombamentoPesquisado = this.tombamento;
   this.model.titulo = this.normalizeTitulo(escolhido.titulo);
   this.exemplarDescricao = this.formatExemplarDescricao(this.tombamento, this.model.titulo);

   // Aviso na hora da escolha, e não só ao salvar: o bibliotecário ainda tem a lista aberta
   // para pegar outro exemplar da mesma obra.
   if (!this.isEdicao() && escolhido.emprestado) {
    this.avisoExemplar = "Este exemplar está emprestado.";
   } else if (!escolhido.podeEmprestar) {
    this.avisoExemplar = "O cadastro deste exemplar não permite empréstimo.";
   }
  },
  isExemplarEmprestado() {
   const emprestado = (this.exemplar as any)?.emprestado;
   return emprestado === true || Number(emprestado) === 1;
  },
  /** A biblioteca da tela: 0 é a central. */
  getGesEscolaTela() {
   const isCentral =
    this.$route.path.includes("/emprestimo/0/") ||
    this.$route.name === "bibemprestimo_grid_central";

   return isCentral ? 0 : Number(useStore().getters.getCodigoEscola || 0);
  },
  isEdicao() {
   return Number(this.model?.bibEmprestimoId || 0) > 0;
  },
  async excluir() {
   this.mensagemErro = "";

   const bibEmprestimoId = Number(this.model?.bibEmprestimoId || 0);
   if (!bibEmprestimoId) {
    return;
   }

   const confirmou = await dialogoService.confirmar({
    mensagem: 'Deseja excluir esse empréstimo?',
    textoConfirmar: 'Excluir',
    perigo: true,
   });
   if (!confirmou) {
    return;
   }

   this.excluindo = true;
   try {
    await BibEmprestimoService.remover(bibEmprestimoId);
    this.close();
    this.$emit("saved");
   } catch (error) {
    this.mensagemErro = "Ocorreu um erro ao excluir o empr\u00E9stimo.";
    console.error("Erro ao excluir empr\u00E9stimo:", error);
   } finally {
    this.excluindo = false;
   }
  },
  async salvar() {
   this.mensagemErro = "";

   if (!this.exemplar?.bibExemplarId) {
    this.mensagemErro = "Pesquise e selecione o exemplar.";
    return;
   }

   // A busca só oferece o acervo desta biblioteca, então "exemplar de outra biblioteca" deixou
   // de ser alcançável pela tela. O que sobra é a trava de exemplar já emprestado.
   const isNovoEmprestimo = Number(this.model.bibEmprestimoId || 0) === 0;
   if (isNovoEmprestimo && this.isExemplarEmprestado()) {
    this.mensagemErro = "Este exemplar já está emprestado.";
    return;
   }

   if (!this.form.dataEmprestimo) {
    this.mensagemErro = "Informe a data de empréstimo.";
    return;
   }

   if (!this.form.dataVencimento) {
    this.mensagemErro = "Informe a data de vencimento.";
    return;
   }

   const payload: BibEmprestimoModel = {
    ...this.model,
    bibEmprestimoId: Number(this.model.bibEmprestimoId) || 0,
    bibExemplarId: Number(this.exemplar.bibExemplarId),
    segUsuarioId: this.contexto.segUsuarioId,
    bibTipoUsuarioId: this.contexto.bibTipoUsuarioId,
    especial: this.especialMarcado ? 1 : 0,
    dataEmprestimo: this.form.dataEmprestimo as unknown as Date,
    dataVencimento: this.form.dataVencimento as unknown as Date,
    tipoEmprestimo: 0,
    observacao: this.form.observacao ?? "",
    situacao: Number(this.model.situacao) || 0,
    devolvido: Number(this.model.devolvido) || 0,
    renovacoes: Number(this.model.renovacoes) || 0,
    // A biblioteca em que o empréstimo foi feito. Faltava no payload, e a coluna nascia NULL em
    // todo empréstimo — a listagem da escola filtrava por ela e não achava nada.
    gesEscolaId: this.getGesEscolaTela(),
   };

   if (!payload.bibEmprestimoId) {
    payload.situacao = 0;
    payload.devolvido = 0;
    payload.renovacoes = 0;
   }

   this.salvando = true;
   try {
    await BibEmprestimoService.inserirAtualizar(payload);
    this.close();
    this.$emit("saved");
   } catch (error) {
    this.mensagemErro = "Ocorreu um erro ao salvar o empréstimo.";
    console.error("Erro ao salvar empréstimo:", error);
   } finally {
    this.salvando = false;
   }
  },
 },
});
</script>
